import React from 'react'

import {
  ComparableProperty,
  LotProperty,
  PropertyPositionInCompareMode,
  RequiredComparableProperty,
  UnitProperty,
} from '@adUtilities/types'

export type ComparablePropertiesMap = Record<
  number,
  ComparableProperty['property']
>

interface InitialValues {
  comparableProperties?: ComparableProperty[]
  active?: boolean
}

interface UseComparePropertyArgs {
  initialValues?: InitialValues
  maxComparisonCount?: number
  disable?: boolean
}

interface ValidationResult {
  validated: boolean
  data: RequiredComparableProperty | undefined
}

type PropertyWithoutReferences =
  | Omit<UnitProperty, 'references'>
  | Omit<LotProperty, 'references'>

const DEFAULT_MAX_COMPARISON_COUNT = 2

const useCompareProperty = (args?: UseComparePropertyArgs) => {
  const {
    initialValues,
    maxComparisonCount = DEFAULT_MAX_COMPARISON_COUNT,
    disable,
  } = args || {}

  const initializeComparableProperties = React.useCallback<
    () => ComparablePropertiesMap
  >(
    () =>
      [...Array(maxComparisonCount)].reduce<ComparablePropertiesMap>(
        (a, _, index) => {
          const matchedValue = initialValues?.comparableProperties?.find(
            (item) => item.index === index
          )
          /* eslint-disable-next-line no-param-reassign */
          a[index] = matchedValue ? matchedValue.property : null
          return a
        },
        {}
      ),
    [maxComparisonCount, initialValues?.comparableProperties]
  )

  const [comparablePropertiesMap, setComparablePropertiesMap] =
    React.useState<ComparablePropertiesMap>(initializeComparableProperties())
  const [active, setActive] = React.useState<boolean>(!!initialValues?.active)

  const compareModeIsActive = React.useMemo(
    () => !disable && active,
    [disable, active]
  )

  const [
    initialPropertyAtComparisonStart,
    setInitialPropertyAtComparisonStart,
  ] = React.useState<UnitProperty | LotProperty>()

  const generateComparablePropertiesFromMap = React.useCallback(
    (map: ComparablePropertiesMap) =>
      Object.keys(map)
        .map<ComparableProperty>((key) => {
          const index = Number(key)
          return {
            index,
            property: map[index],
          }
        })
        .sort((a, b) => a.index - b.index),
    []
  )

  const comparableProperties = React.useMemo<ComparableProperty[]>(
    () =>
      disable
        ? []
        : generateComparablePropertiesFromMap(comparablePropertiesMap),
    [comparablePropertiesMap, generateComparablePropertiesFromMap, disable]
  )

  const checkPropertyComparability = React.useCallback<
    (property: PropertyWithoutReferences) => boolean
  >(
    (property) => {
      if (disable) {
        return false
      }
      if (!property) {
        return false
      }
      return true
    },
    [disable]
  )

  const setComparableProperties = React.useCallback(
    (values: ComparableProperty[]) => {
      const newValue = Object.keys(
        comparablePropertiesMap
      ).reduce<ComparablePropertiesMap>((a, c) => {
        const index = Number(c)
        const matchedValue = values?.find((item) => item.index === index)
        /* eslint-disable-next-line no-param-reassign */
        a[index] = matchedValue ? matchedValue.property : null
        return a
      }, {})
      setComparablePropertiesMap(newValue)
    },
    [comparablePropertiesMap]
  )

  const validateAdd = React.useCallback<
    (args: {
      property: RequiredComparableProperty['property']
      index?: number
      preventDuplicate?: boolean
      replaceLast?: boolean
    }) => ValidationResult
  >(
    ({ property, index, preventDuplicate = true, replaceLast = true }) => {
      const validationResult: ValidationResult = {
        validated: false,
        data: undefined,
      }
      const isDuplicate = comparableProperties.some(
        (item) => item.property?.data?.id === property.data.id
      )
      const comparisonCount = comparableProperties.filter(
        (item) => !!item.property
      ).length
      const comparisonCountReached = comparisonCount >= maxComparisonCount

      if (preventDuplicate && isDuplicate) {
        return validationResult
      }

      if (comparisonCountReached && !replaceLast) {
        return validationResult
      }

      let newIndex = comparableProperties?.find((item) => !item.property)?.index

      if (replaceLast && comparisonCountReached) {
        newIndex = comparableProperties.length - 1
      }

      if (index !== undefined) {
        newIndex = index
      }

      if (newIndex !== undefined) {
        validationResult.validated = true
        validationResult.data = {
          index: newIndex,
          property,
        }
      }

      return validationResult
    },
    [comparableProperties, maxComparisonCount]
  )

  const validateReplace = React.useCallback<
    (args: {
      property: RequiredComparableProperty['property']
      index: number
      preventDuplicate?: boolean
    }) => ValidationResult
  >(
    ({ property, index, preventDuplicate = true }) => {
      const validationResult: ValidationResult = {
        validated: false,
        data: undefined,
      }
      if (
        preventDuplicate &&
        comparableProperties.some(
          (item) => item.property?.data?.id === property.data.id
        )
      ) {
        return validationResult
      }

      validationResult.validated = true
      validationResult.data = {
        index,
        property,
      }

      return validationResult
    },
    [comparableProperties, maxComparisonCount]
  )

  const validateRemove = React.useCallback<
    (args: { index: number; keepLast?: boolean }) => ValidationResult
  >(
    ({ index, keepLast }) => {
      const validationResult: ValidationResult = {
        validated: false,
        data: undefined,
      }

      if (!comparablePropertiesMap[index]) {
        return validationResult
      }

      if (!keepLast) {
        validationResult.validated = true
        return validationResult
      }

      if (comparableProperties.filter((item) => !!item.property).length > 1) {
        validationResult.validated = true
        return validationResult
      }

      return validationResult
    },
    [comparableProperties, comparablePropertiesMap]
  )

  const addToComparison = React.useCallback(
    (args: {
      property: RequiredComparableProperty['property']
      index?: number
      preventDuplicate?: boolean
      replaceLast?: boolean
    }) => {
      const { validated, data: validatedData } = validateAdd(args)

      if (!(validated && validatedData)) {
        return
      }

      setComparablePropertiesMap((prevVal) => ({
        ...prevVal,
        [validatedData.index]: validatedData.property,
      }))
    },
    [validateAdd]
  )

  const startComparison = React.useCallback(
    (args?: Partial<ComparableProperty>) => {
      const { property, index } = args || {}
      if (property) {
        addToComparison({
          property,
          index,
        })
        setInitialPropertyAtComparisonStart(property)
      }
      setActive(true)
    },
    [addToComparison]
  )

  const replaceInComparison = React.useCallback(
    (args: {
      property: RequiredComparableProperty['property']
      index: number
      preventDuplicate?: boolean
    }) => {
      const { validated, data: validatedData } = validateReplace(args)

      if (!(validated && validatedData)) {
        return
      }

      setComparablePropertiesMap((prevVal) => ({
        ...prevVal,
        [validatedData.index]: validatedData.property,
      }))
    },
    [validateReplace]
  )

  const removeFromComparison = React.useCallback(
    (args: { index: number; keepLast?: boolean }) => {
      const { index } = args
      const { validated } = validateRemove(args)
      if (!validated) {
        return
      }
      setComparablePropertiesMap((prevVal) => ({
        ...prevVal,
        [index]: null,
      }))
    },
    [validateRemove]
  )

  const stopComparison = React.useCallback(() => {
    setActive(false)
    setComparablePropertiesMap(initializeComparableProperties())
    setInitialPropertyAtComparisonStart(undefined)
  }, [initializeComparableProperties])

  const checkPropertyPositionInCompareMode = React.useCallback<
    (propertyId: string) =>
      | {
          position: PropertyPositionInCompareMode
          index: number
        }
      | undefined
  >(
    (propertyId) => {
      if (comparableProperties?.[0]?.property?.data?.id === propertyId) {
        return {
          index: 0,
          position: PropertyPositionInCompareMode.First,
        }
      }
      if (
        comparableProperties?.[maxComparisonCount - 1]?.property?.data?.id ===
        propertyId
      ) {
        return {
          index: maxComparisonCount - 1,
          position: PropertyPositionInCompareMode.Last,
        }
      }
      return undefined
    },
    [comparableProperties, maxComparisonCount]
  )

  const hasEmptyComparableSlot = React.useMemo(
    () => comparableProperties.some((item) => !item.property),
    [comparableProperties]
  )

  return React.useMemo(
    () => ({
      checkPropertyComparability,
      compareModeIsActive,
      comparableProperties,
      initialPropertyAtComparisonStart,
      setInitialPropertyAtComparisonStart,
      setComparableProperties,
      startComparison,
      stopComparison,
      addToComparison,
      replaceInComparison,
      removeFromComparison,
      validateAdd,
      validateRemove,
      validateReplace,
      checkPropertyPositionInCompareMode,
      hasEmptyComparableSlot,
    }),
    [
      checkPropertyComparability,
      compareModeIsActive,
      comparableProperties,
      initialPropertyAtComparisonStart,
      setInitialPropertyAtComparisonStart,
      setComparableProperties,
      startComparison,
      stopComparison,
      addToComparison,
      replaceInComparison,
      removeFromComparison,
      validateAdd,
      validateRemove,
      validateReplace,
      checkPropertyPositionInCompareMode,
      hasEmptyComparableSlot,
    ]
  )
}

export default useCompareProperty

export type { ComparableProperty }
